// frontend/src/App.js - UPDATED WITH WAITLIST + COOKIES PAGE 
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ApolloProvider } from '@apollo/client';
import client from './apollo/client';

// Context Providers
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { NotificationProvider } from './context/NotificationContext';
import { CartProvider } from './context/CartContext';
import { StripeProvider } from './context/StripeContext';
import { TrustProvider } from './context/TrustContext';
import { SkillProvider } from './context/SkillContext';
import { AdminProvider } from './context/AdminContext';
import { JobsProvider } from './context/JobsContext';
import { TokenProvider } from './context/TokenContext';
import { MessagingProvider } from './context/MessagingContext';
import { ResourceProvider } from './context/ResourceContext';
import { AchievementProvider } from './context/AchievementContext';
import AchievementCelebration from './components/achievements/AchievementCelebration';


// Pages
import QuickIntro from './components/onboarding/QuickIntro';
import HomePage from './pages/HomePage';
import WaitlistPage from './pages/WaitlistPage'; // ← ADDED: Founding Member waitlist page
import GroupsPage from './pages/GroupsPage';
import GroupDetailPage from './pages/GroupDetailPage';
import ProductDetailPage from './pages/ProductDetailPage';
import CreateGroupPage from './pages/CreateGroupPage';
import ProfilePage from './pages/ProfilePage';
import SettingsPage from './pages/SettingsPage';
import SkillsPage from './pages/SkillsPage';
import GroupAdminPage from './pages/GroupAdminPage';
import WalletPage from './pages/WalletPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import LogisticsPage from './pages/LogisticsPage';
import Login from './pages/Login';
import Register from './pages/Register';
import Tutorial from './pages/Tutorial';
import VendorLogin from './pages/VendorLogin';
import VendorRegister from './pages/VendorRegister';
import VendorDashboard from './pages/VendorDashboard';
import VendorOnboardingPage from './pages/VendorOnboardingPage';
import VendorShowcasePage from './pages/VendorShowcasePage';
import BusinessInfoPage from './pages/BusinessInfoPage';
import AnalyticsDashboardPage from './pages/AnalyticsDashboardPage';
import VendorBulkOrder from './pages/VendorBulkOrder';
import VendorGroupDiscovery from './pages/VendorGroupDiscovery';
import VendorGuide from './components/help/VendorGuide';
import SupportPage from './components/help/SupportPage';
import OrdersPage from './pages/OrdersPage';
import OrderConfirmationPage from './pages/OrderConfirmationPage';
import DealsPage from './pages/DealsPage';
import EarnPage from './pages/EarnPage';
import CustomerSupportPage from './pages/CustomerSupportPage';
import TransparencyPage from './pages/TransparencyPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import CookiePolicyPage from './pages/CookiePolicyPage';
import SearchResultsPage from './pages/SearchResultsPage';
import AdminSupportDashboard from './admin/pages/AdminSupportDashboard';
import AdminLogin from './admin/pages/AdminLogin';
import AdminDashboard from './admin/pages/AdminDashboard';
import AdminLayout from './admin/components/AdminLayout';
import AdminUsers from './admin/pages/AdminUsers';
import AdminProducts from './admin/pages/AdminProducts';
import AdminAnalytics from './admin/pages/AdminAnalytics';
import AdminSystem from './admin/pages/AdminSystem';
import AdminSecurityLogs from './admin/pages/AdminSecurityLogs';
import AdminSettings from './admin/pages/AdminSettings';
import AdminOrders from './admin/pages/AdminOrders';
import AdminGroups from './admin/pages/AdminGroups';
import AdminJobs from './admin/pages/AdminJobs';
import AdminSkills from './admin/pages/AdminSkills';
import AdminTokens from './admin/pages/AdminTokens';

// Components
import WelcomeScreen from './components/onboarding/WelcomeScreen';
import AvaAssistant from './components/onboarding/AvaAssistant';

// Utils
import { initializeAllDemoData } from './utils/groupData';

// Initialize demo data
initializeAllDemoData();

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true' || 
                         localStorage.getItem('isLoggedIn') === 'true';
  
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  
  return children;
};

// Vendor Route Protection
const VendorRoute = ({ children }) => {
  const userType = localStorage.getItem('userType');
  const isVendor = userType === 'vendor';
  
  console.log('🔍 VendorRoute check:', {
    userType,
    isVendor,
    willRedirect: !isVendor
  });

  if (!isVendor) {
    console.log('❌ VendorRoute: Redirecting to login!');
    return <Navigate to="/vendor/login" />;
  }
  
  console.log('✅ VendorRoute: Allowing access!');
  return children;
};

// Admin Route Protection
const AdminRoute = ({ children }) => {
  const isAdminAuth = localStorage.getItem('isAdminAuthenticated') === 'true';
  const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
  
  if (!isAdminAuth || adminUser.role !== 'ADMIN') {
    return <Navigate to="/admin/login" />;
  }
  
  return children;
};

// Default Route Logic
// ← MODIFIED: Unauthenticated users now see WaitlistPage instead of redirecting to /welcome
const DefaultRoute = () => {
  const isAdminAuth = localStorage.getItem('isAdminAuthenticated') === 'true';
  const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true' || 
                         localStorage.getItem('isLoggedIn') === 'true';
  const userType = localStorage.getItem('userType');
  
  // Check admin first to prevent conflicts
  if (isAdminAuth) {
    return <Navigate to="/admin/dashboard" />;
  }
  
  if (isAuthenticated) {
    if (userType === 'vendor') {
      return <Navigate to="/vendor/dashboard" />;
    }
    return <Navigate to="/home" />;
  }
  
  // ← CHANGED: Was <Navigate to="/welcome" /> — now renders waitlist directly
  return <WaitlistPage />;
};

function App() {
  return (
    <ApolloProvider client={client}>
      <Router>
        <AuthProvider>
          <ThemeProvider>
            <NotificationProvider>
              <CartProvider>
                <StripeProvider>
                  <TrustProvider>
                    <SkillProvider>
                      <AdminProvider>
                        <TokenProvider>
                          <MessagingProvider>
                            <JobsProvider>
                              <ResourceProvider>
                                <AchievementProvider>
                                  <div className="App">
                                    <Routes>
                                      {/* Default Route */}
                                      <Route path="/" element={<DefaultRoute />} />
                                  
                                      {/* Public Routes */}
                                      <Route path="/welcome" element={<WelcomeScreen />} />
                                      <Route path="/login" element={<Login />} />
                                      <Route path="/register" element={<Register />} />
                                      <Route path="/tutorial" element={<Tutorial />} />
                                      <Route path="/quick-intro" element={<QuickIntro />} />
                                      <Route path="/business-info" element={<BusinessInfoPage />} />
                                      
                                      {/* PUBLIC: Transparency Page (No auth required) */}
                                      <Route path="/transparency" element={<TransparencyPage />} />

                                      {/* PUBLIC: Legal Pages (No auth required) */}
                                      <Route path="/privacy" element={<PrivacyPolicyPage />} />
                                      <Route path="/terms" element={<TermsOfServicePage />} />
                                      <Route path="/cookies" element={<CookiePolicyPage />} />
                                  
                                      {/* Vendor Routes */}
                                      <Route path="/vendor/login" element={<VendorLogin />} />
                                      <Route path="/vendor/register" element={<VendorRegister />} />
                                      <Route path="/vendors" element={<Navigate to="/vendor/register" replace />} />
                                  
                                      <Route path="/vendor/onboarding" element={
                                        <VendorRoute>
                                          <VendorOnboardingPage />
                                        </VendorRoute>
                                      } />
                                      <Route path="/vendor/groups" element={
                                        <VendorRoute>
                                          <VendorGroupDiscovery />
                                        </VendorRoute>
                                      } />
                                      <Route path="/vendor/bulk-order" element={
                                        <VendorRoute>
                                          <VendorBulkOrder />
                                        </VendorRoute>
                                      } />
                                      <Route path="/vendor/bulk-order/:groupId" element={
                                        <VendorRoute>
                                          <VendorBulkOrder />
                                        </VendorRoute>
                                      } />
                                      <Route path="/vendor/dashboard" element={
                                        <VendorRoute>
                                          <VendorDashboard />
                                        </VendorRoute>
                                      } />
                                      <Route path="/vendor/showcase" element={<VendorShowcasePage />} />
                                  
                                      {/* Admin Routes - Completely separate from main application */}
                                      <Route path="/admin/login" element={<AdminLogin />} />
                                      <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
                                        <Route index element={<Navigate to="/admin/dashboard" />} />
                                        <Route path="dashboard" element={<AdminDashboard />} />
                                        <Route path="support" element={<AdminSupportDashboard />} />
                                        <Route path="users" element={<AdminUsers />} />
                                        <Route path="products" element={<AdminProducts />} />
                                        <Route path="analytics" element={<AdminAnalytics />} />
                                        <Route path="system" element={<AdminSystem />} />
                                        <Route path="security" element={<AdminSecurityLogs />} />
                                        <Route path="settings" element={<AdminSettings />} />
                                        <Route path="orders" element={<AdminOrders />} />
                                        <Route path="groups" element={<AdminGroups />} />
                                        <Route path="jobs" element={<AdminJobs />} />
                                        <Route path="skills" element={<AdminSkills />} />
                                        <Route path="tokens" element={<AdminTokens />} />
                                      </Route>
                                      <Route path="/search" element={
                                        <ProtectedRoute>
                                          <SearchResultsPage />
                                        </ProtectedRoute>
                                      } />
                                  
                                      {/* Help Routes with Proper Navigation */}
                                      <Route path="/help/vendor-guide" element={<VendorGuide />} />
                                      <Route path="/help/vendor-best-practices" element={<VendorGuide />} />
                                      <Route path="/help/support" element={<SupportPage />} />
                                  
                                      {/* Protected Member Routes */}
                                      <Route path="/home" element={
                                        <ProtectedRoute>
                                          <HomePage />
                                        </ProtectedRoute>
                                      } />
                                      <Route path="/deals" element={
                                        <ProtectedRoute>
                                          <DealsPage />
                                        </ProtectedRoute>
                                      } />
                                      <Route path="/groups/:id" element={
                                        <ProtectedRoute>
                                          <GroupDetailPage />
                                        </ProtectedRoute>
                                      } />
                                      <Route path="/product/:id" element={
                                        <ProtectedRoute>
                                          <ProductDetailPage />
                                        </ProtectedRoute>
                                      } />
                                      <Route path="/groups/:id/admin" element={
                                        <ProtectedRoute>
                                          <GroupAdminPage />
                                        </ProtectedRoute>
                                      } />
                                      <Route path="/create-group" element={
                                        <ProtectedRoute>
                                          <CreateGroupPage />
                                        </ProtectedRoute>
                                      } />
                                      <Route path="/earn" element={
                                        <ProtectedRoute>
                                          <EarnPage />
                                        </ProtectedRoute>
                                      } />
                                      <Route path="/profile" element={
                                        <ProtectedRoute>
                                          <ProfilePage />
                                        </ProtectedRoute>
                                      } />
                                      <Route path="/settings" element={
                                        <ProtectedRoute>
                                          <SettingsPage />
                                        </ProtectedRoute>
                                      } />
                                      <Route path="/skills" element={
                                        <ProtectedRoute>
                                          <SkillsPage />
                                        </ProtectedRoute>
                                      } />
                                      <Route path="/wallet" element={
                                        <ProtectedRoute>
                                          <WalletPage />
                                        </ProtectedRoute>
                                      } />
                                      <Route path="/cart" element={
                                        <ProtectedRoute>
                                          <CartPage />
                                        </ProtectedRoute>
                                      } />
                                      <Route path="/checkout" element={
                                        <ProtectedRoute>
                                          <CheckoutPage />
                                        </ProtectedRoute>
                                      } />
                                      <Route path="/orders" element={
                                        <ProtectedRoute>
                                          <OrdersPage />
                                        </ProtectedRoute>
                                      } />
                                      <Route path="/order-confirmation/:orderId" element={
                                        <ProtectedRoute>
                                          <OrderConfirmationPage />
                                        </ProtectedRoute>
                                      } />
                                      <Route path="/support" element={
                                        <ProtectedRoute>
                                        <CustomerSupportPage />
                                        </ProtectedRoute>
                                      } />
                                      <Route path="/logistics" element={<Navigate to="/earn" replace />} />
                                      <Route path="/analytics" element={
                                        <ProtectedRoute>
                                          <AnalyticsDashboardPage />
                                        </ProtectedRoute>
                                      } />
                                    </Routes>
                                
                                    {/* Ava Assistant - Available throughout the app */}
                                    <AvaAssistant />
                                    
                                    {/* Achievement Celebration - Global popup */}
                                    <AchievementCelebration />
                                  </div>
                                </AchievementProvider>
                              </ResourceProvider>
                            </JobsProvider>
                          </MessagingProvider>
                        </TokenProvider>
                      </AdminProvider>
                    </SkillProvider>
                  </TrustProvider>
                </StripeProvider>
              </CartProvider>
            </NotificationProvider>
          </ThemeProvider>
        </AuthProvider>
      </Router>
    </ApolloProvider>
  );
}

export default App;